﻿/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, {useCallback} from 'react'
import {Header} from '@tanstack/react-table'
import {useTranslation} from 'react-i18next'
import {MagnifyingGlassIcon} from '@heroicons/react/24/outline'
import UiInput from 'components/Ui/UiInput'
import UiTanstackTable from 'components/Ui/UiTanstackTable'
import {currencyNumberFormat} from 'utils/utility'
import {Offer} from 'types/entities/Offer'
import {OfferListRow} from './LeadOffersListTypes'
import {useLeadOffersList} from 'utils/hooks/useLeadOffersList'
import LeadOffersListHeaderFilter from './LeadOffersListHeaderFilter'

interface LeadOffersListProps {
    leadId: number
    offers?: Offer[]
}

const LeadOffersList: React.FC<LeadOffersListProps> = ({ leadId, offers }) => {
    const { t } = useTranslation()
    const {
        tableInit,
        statusOptions,
        accountTypeOptions,
        totalPersons,
        totalPrice,
        openOfferDetail,
        resetPage,
    } = useLeadOffersList(leadId, offers)

    const renderHeaderFilter = useCallback(
        (header: Header<OfferListRow, unknown>) => (
            <LeadOffersListHeaderFilter
                header={header}
                statusOptions={statusOptions}
                accountTypeOptions={accountTypeOptions}
                onResetPage={resetPage}
            />
        ),
        [statusOptions, accountTypeOptions, resetPage]
    )

    return (
        <div className="LeadOffersList w-full flex-1 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800">
                <UiInput
                    placeholder={t('Search offers...')}
                    startIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
                    value={tableInit.globalFilter || ''}
                    onChange={(e) => tableInit.setGlobalFilter(e.target.value)}
                    className="max-w-sm"
                />
            </div>
            <UiTanstackTable
                tableInit={tableInit}
                tableMinHeightClass="min-h-[300px]"
                onRowClick={(row) => openOfferDetail(row.original.offer)}
                renderHeaderFilter={renderHeaderFilter}
                paginationIdPrefix={'lead-offers-pagination'}
                renderFooter={() => (
                    <div className="bg-gray-50/50 dark:bg-gray-800/50 px-6 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-8 text-sm">
                        <div className="flex items-center gap-2">
                            <span className="text-gray-500 font-medium">{t('Sum Persons')}:</span>
                            <span className="font-bold text-gray-900 dark:text-white">{totalPersons}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-500 font-medium">{t('Sum Revenue')}:</span>
                            <span className="font-bold text-gray-900 dark:text-white">
                                {currencyNumberFormat(totalPrice)}
                            </span>
                        </div>
                    </div>
                )}
            />
        </div>
    )
}

export default LeadOffersList
